import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useProducts } from "@/hooks/useProducts";
import { usePriceBooks } from "@/hooks/usePriceBooks";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { ImageUpload } from "./ImageUpload";
import { SellingPointsEditor } from "./SellingPointsEditor";
import { CalculationConfigEditor } from "./CalculationConfigEditor";
import { DollarSign } from "lucide-react";
import { INSTALLATION_PHASES } from "@/utils/installationPhases";
import type { Database } from "@/integrations/supabase/types";

type Product = Database["public"]["Tables"]["products"]["Row"];
type ProductFormData = {
  sku: string;
  name: string;
  brand?: string;
  category: string;
  subcategory?: string;
  unit: string;
  description?: string;
  customer_description?: string;
  selling_points?: string[];
  image_url?: string;
  showcase_image_url?: string;
  is_active: boolean;
  specifications?: any;
  install_phase?: string | null;
  measurement_field?: string | null;
  calculation_config?: any;
};

const CATEGORIES = [
  "Shingles",
  "Ridge Cap",
  "Ice & Water Shield",
  "Underlayment",
  "Starter Strip",
  "Step Flashing",
  "Buy Boots",
  "Chimney",
  "Skylights",
  "Ventilation",
  "Drip Edge",
  "Deck Repair",
  "Tear-Off",
  "Valley",
  "Flashing",
  "Gutters",
  "Cleanup",
  "Warranty",
  "Shingle Colors",
  "Accessories",
  "Labor",
  "Other",
];

const UNITS = [
  { value: "square", label: "Square" },
  { value: "bundle", label: "Bundle" },
  { value: "lf", label: "Linear Foot" },
  { value: "sf", label: "Square Foot" },
  { value: "each", label: "Each" },
  { value: "hour", label: "Hour" },
];

interface ProductDrawerProps {
  open: boolean;
  onClose: () => void;
  product?: Product | null;
}

export function ProductDrawer({ open, onClose, product }: ProductDrawerProps) {
  const { createProduct, updateProduct } = useProducts();
  const { priceBooks, productPrices, updatePrice } = usePriceBooks();
  const isEditing = !!product;
  const [prices, setPrices] = useState<Record<string, { unit_price: string; cost?: string }>>({});

  const form = useForm<ProductFormData>({
    defaultValues: {
      sku: "",
      name: "",
      brand: "",
      category: "Shingles",
      subcategory: "",
      unit: "square",
      description: "",
      customer_description: "",
      selling_points: [],
      image_url: "",
      showcase_image_url: "",
      is_active: true,
      install_phase: null,
      measurement_field: null,
      calculation_config: null,
    },
  });

  useEffect(() => {
    if (product) {
      form.reset({
        sku: product.sku,
        name: product.name,
        brand: product.brand || "",
        category: product.category,
        subcategory: product.subcategory || "",
        unit: product.unit,
        description: product.description || "",
        customer_description: product.customer_description || "",
        selling_points: (product.selling_points as string[]) || [],
        image_url: product.image_url || "",
        showcase_image_url: product.showcase_image_url || "",
        is_active: product.is_active,
        install_phase: product.install_phase || null,
        measurement_field: product.measurement_field || null,
        calculation_config: product.calculation_config || null,
      });

      // Load existing prices for this product
      if (productPrices) {
        const existingPrices: Record<string, { unit_price: string; cost?: string }> = {};
        productPrices
          .filter(pp => pp.product_id === product.id)
          .forEach(pp => {
            existingPrices[pp.price_book_id] = {
              unit_price: pp.unit_price?.toString() || "",
              cost: pp.cost?.toString() || "",
            };
          });
        setPrices(existingPrices);
      }
    } else {
      form.reset({
        sku: "",
        name: "",
        brand: "",
        category: "Shingles",
        subcategory: "",
        unit: "square",
        description: "",
        customer_description: "",
        selling_points: [],
        image_url: "",
        showcase_image_url: "",
        is_active: true,
        install_phase: null,
        measurement_field: null,
        calculation_config: null,
      });
      setPrices({});
    }
  }, [product, form, productPrices]);

  const onSubmit = async (data: ProductFormData) => {
    let productId: string;
    
    if (isEditing && product) {
      await updateProduct.mutateAsync({ id: product.id, ...data });
      productId = product.id;
    } else {
      const newProduct = await createProduct.mutateAsync(data);
      productId = newProduct.id;
    }

    // Save prices if any were entered (skip for Shingle Colors)
    if (data.category !== 'Shingle Colors') {
      const priceUpdates = Object.entries(prices)
        .filter(([_, price]) => price.unit_price && parseFloat(price.unit_price) > 0)
        .map(([priceBookId, price]) => ({
          product_id: productId,
          price_book_id: priceBookId,
          unit_price: parseFloat(price.unit_price),
          cost: price.cost ? parseFloat(price.cost) : undefined,
        }));

      for (const priceUpdate of priceUpdates) {
        await updatePrice.mutateAsync(priceUpdate);
      }
    }
    
    onClose();
  };

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="sm:max-w-[540px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>{isEditing ? "Edit Product" : "Add New Product"}</SheetTitle>
          <SheetDescription>
            {isEditing
              ? "Update product details and specifications"
              : "Add a new product to your catalog"}
          </SheetDescription>
        </SheetHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6 mt-6">
            <FormField
              control={form.control}
              name="sku"
              rules={{ required: "SKU is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>SKU *</FormLabel>
                  <FormControl>
                    <Input placeholder="SHNG-001" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="name"
              rules={{ required: "Name is required" }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Product Name *</FormLabel>
                  <FormControl>
                    <Input placeholder="Architectural Shingles" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="brand"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Brand</FormLabel>
                  <FormControl>
                    <Input placeholder="GAF, Owens Corning, CertainTeed" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="category"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Category *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {CATEGORIES.map((cat) => (
                          <SelectItem key={cat} value={cat}>
                            {cat}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="unit"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Unit *</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {UNITS.map((unit) => (
                          <SelectItem key={unit.value} value={unit.value}>
                            {unit.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="install_phase"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Installation Phase (Optional)</FormLabel>
                  <Select 
                    onValueChange={(value) => field.onChange(value === "none" ? null : value)} 
                    value={field.value || "none"}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a phase" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="none">None (Not phase-specific)</SelectItem>
                      {INSTALLATION_PHASES.map((phase) => (
                        <SelectItem key={phase.key} value={phase.key}>
                          Phase {phase.number}: {phase.title}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-sm text-muted-foreground">
                    Assign this product to a specific installation step in presentations
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <Separator className="my-6" />

            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold">Product Calculation Configuration</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Configure how this product's quantity is calculated from measurements
                </p>
              </div>
              
              <CalculationConfigEditor
                value={form.watch('calculation_config')}
                onChange={(config) => form.setValue('calculation_config', config)}
                category={form.watch('category')}
                unit={form.watch('unit')}
              />
            </div>

            <Separator className="my-6" />

            <FormField
              control={form.control}
              name="measurement_field"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Legacy Formula (Optional - For Backward Compatibility)</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value || ''}
                      placeholder="e.g., area_sqft/100*1.1 or (eave_lf+rake_lf)*1.1"
                      className="font-mono text-sm"
                    />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    ℹ️ Use the Calculation Configuration above for new products. This field is for legacy formulas only.
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Product specifications and details..."
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="customer_description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Customer-Facing Description</FormLabel>
                  <FormControl>
                    <Textarea
                      placeholder="Describe this product in customer-friendly language. This appears in proposals."
                      rows={3}
                      {...field}
                    />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    Example: "Premium architectural shingles with enhanced wind resistance and lifetime warranty coverage."
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-2">
              <Label>Selling Points (Bullet List)</Label>
              <SellingPointsEditor
                value={form.watch('selling_points') || []}
                onChange={(points) => form.setValue('selling_points', points)}
              />
              <p className="text-xs text-muted-foreground">
                Add 3-5 key benefits or features. These appear as bullets in the proposal.
              </p>
            </div>

            {/* Coverage specifications for Underlayment */}
            {form.watch('category')?.toLowerCase().includes('underlayment') && (
              <FormItem>
                <FormLabel>Coverage per Roll (sq ft)</FormLabel>
                <FormControl>
                  <Input 
                    type="number"
                    placeholder="1000"
                    defaultValue={(product?.specifications as any)?.coverage_per_unit || "1000"}
                    onChange={(e) => {
                      const currentSpecs = (form.getValues('specifications') as any) || {};
                      form.setValue('specifications', {
                        ...currentSpecs,
                        coverage_per_unit: parseInt(e.target.value) || 1000
                      });
                    }}
                  />
                </FormControl>
                <p className="text-sm text-muted-foreground">
                  Default: 1,000 sq ft (10 squares per roll)
                </p>
              </FormItem>
            )}

            {/* Roll dimensions for Ice & Water Shield */}
            {(form.watch('category')?.toLowerCase().includes('ice') || 
              form.watch('category')?.toLowerCase().includes('water') ||
              form.watch('name')?.toLowerCase().includes('ice')) && (
              <div className="space-y-4 p-4 border rounded-lg">
                <p className="text-sm font-medium">Roll Dimensions</p>
                <div className="grid grid-cols-2 gap-4">
                  <FormItem>
                    <FormLabel>Width (ft)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number"
                        step="0.1"
                        placeholder="3"
                        defaultValue={(product?.specifications as any)?.roll_dimensions?.width || "3"}
                        onChange={(e) => {
                          const currentSpecs = (form.getValues('specifications') as any) || {};
                          const width = parseFloat(e.target.value) || 3;
                          const length = currentSpecs.roll_dimensions?.length || 33;
                          form.setValue('specifications', {
                            ...currentSpecs,
                            roll_dimensions: {
                              width,
                              length,
                              coverage: width * length
                            }
                          });
                        }}
                      />
                    </FormControl>
                  </FormItem>
                  <FormItem>
                    <FormLabel>Length (ft)</FormLabel>
                    <FormControl>
                      <Input 
                        type="number"
                        step="0.1"
                        placeholder="33"
                        defaultValue={(product?.specifications as any)?.roll_dimensions?.length || "33"}
                        onChange={(e) => {
                          const currentSpecs = (form.getValues('specifications') as any) || {};
                          const length = parseFloat(e.target.value) || 33;
                          const width = currentSpecs.roll_dimensions?.width || 3;
                          form.setValue('specifications', {
                            ...currentSpecs,
                            roll_dimensions: {
                              width,
                              length,
                              coverage: width * length
                            }
                          });
                        }}
                      />
                    </FormControl>
                  </FormItem>
                </div>
                <p className="text-sm text-muted-foreground">
                  Default: 3 ft × 33 ft = 99 sq ft coverage per roll
                </p>
              </div>
            )}

            <FormField
              control={form.control}
              name="image_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Product Image (Thumbnail)</FormLabel>
                  <FormControl>
                    <ImageUpload
                      value={field.value || null}
                      onChange={field.onChange}
                      productId={product?.id}
                    />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    Small thumbnail shown in product grid
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="showcase_image_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Showcase Image (Installation Photo)</FormLabel>
                  <FormControl>
                    <ImageUpload
                      value={field.value || null}
                      onChange={field.onChange}
                      productId={product?.id}
                    />
                  </FormControl>
                  <p className="text-xs text-muted-foreground">
                    Full-resolution installation photo shown during sales presentation (long-press to view)
                  </p>
                  <FormMessage />
                </FormItem>
              )}
            />

            {/* Pricing Section - Skip for Shingle Colors */}
            {form.watch('category') !== 'Shingle Colors' && (
              <>
                <Separator className="my-6" />
                
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-primary" />
                    <div>
                      <h3 className="text-lg font-semibold">Pricing by County</h3>
                      <p className="text-sm text-muted-foreground">
                        Set prices for each price book. You can also set these later in the Pricing tab.
                      </p>
                    </div>
                  </div>

                  {priceBooks?.map((priceBook) => (
                    <div key={priceBook.id} className="p-4 border rounded-lg space-y-3">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">{priceBook.name}</p>
                          <p className="text-sm text-muted-foreground">{priceBook.county} County</p>
                        </div>
                        {prices[priceBook.id]?.unit_price && (
                          <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded">
                            Price Set
                          </span>
                        )}
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm">Unit Price</Label>
                          <div className="flex items-center gap-1 mt-1">
                            <span className="text-muted-foreground">$</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="0.00"
                              value={prices[priceBook.id]?.unit_price || ""}
                              onChange={(e) => {
                                setPrices(prev => ({
                                  ...prev,
                                  [priceBook.id]: {
                                    ...prev[priceBook.id],
                                    unit_price: e.target.value,
                                  }
                                }));
                              }}
                            />
                          </div>
                        </div>

                        <div>
                          <Label className="text-sm">Cost (Optional)</Label>
                          <div className="flex items-center gap-1 mt-1">
                            <span className="text-muted-foreground">$</span>
                            <Input
                              type="number"
                              step="0.01"
                              min="0"
                              placeholder="0.00"
                              value={prices[priceBook.id]?.cost || ""}
                              onChange={(e) => {
                                setPrices(prev => ({
                                  ...prev,
                                  [priceBook.id]: {
                                    ...prev[priceBook.id],
                                    cost: e.target.value,
                                  }
                                }));
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      {prices[priceBook.id]?.unit_price && prices[priceBook.id]?.cost && (
                        <p className="text-xs text-muted-foreground">
                          Margin: {(
                            ((parseFloat(prices[priceBook.id].unit_price) - parseFloat(prices[priceBook.id].cost!)) / 
                            parseFloat(prices[priceBook.id].unit_price)) * 100
                          ).toFixed(1)}%
                        </p>
                      )}
                    </div>
                  ))}
                </div>

                <Separator className="my-6" />
              </>
            )}

            <FormField
              control={form.control}
              name="is_active"
              render={({ field }) => (
                <FormItem className="flex items-center justify-between rounded-lg border p-4">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Active Product</FormLabel>
                    <div className="text-sm text-muted-foreground">
                      Show this product in estimates and price lists
                    </div>
                  </div>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" onClick={onClose} className="flex-1">
                Cancel
              </Button>
              <Button type="submit" className="flex-1">
                {isEditing ? "Update Product" : "Create Product"}
              </Button>
            </div>
          </form>
        </Form>
      </SheetContent>
    </Sheet>
  );
}
